/**
 * Shared types for the plan feature.
 * Mirrors the LimitMetric type in _shared/plan-limits.ts.
 */

/**
 * The enforceable numeric metrics on an org's plan.
 * Maps to columns usage_<metric> and max_<metric> in relay.tenant_plan_limits.
 */
export type LimitMetric =
  | "contacts"
  | "seats"
  | "email_sends_month"
  | "automations"
  | "sequences";

// ---------------------------------------------------------------------------
// Feature gate keys — used as constants across the app.
// Gates only DISABLE: absent key = feature enabled (default-open).
// Agency owners set these on client orgs via relay_agency_set_client_feature_gate.
// ---------------------------------------------------------------------------

export const FEATURE_GATES = {
  social: "social",
  email_campaigns: "email_campaigns",
  sms_campaigns: "sms_campaigns",
  sequences: "sequences",
  forms: "forms",
  bookings: "bookings",
  agency: "agency",
  client_portal: "client_portal",
} as const;

export type FeatureGateKey = typeof FEATURE_GATES[keyof typeof FEATURE_GATES];

// ---------------------------------------------------------------------------
// Feature addon keys — OPPOSITE semantics from FEATURE_GATES.
// Absent key OR any value !== true = module is OFF. Only explicit true enables.
// Used for opt-in modules tenants must turn on explicitly. Agency owners set
// these on client orgs via relay_agency_set_client_feature_addon.
// ---------------------------------------------------------------------------

export const FEATURE_ADDONS = {
  // Relay's ledger (Glorify-LLC/relay#176). Self-service since #183: an org
  // owner or admin turns it on in Settings → Modules.
  ledger: "ledger",
} as const;

export type FeatureAddonKey = typeof FEATURE_ADDONS[keyof typeof FEATURE_ADDONS];

/**
 * Per-addon display metadata for the self-serve Settings → Modules page.
 * Paid/contract entitlements can live in FEATURE_ADDONS without appearing
 * here; those keys must be provisioned by billing/platform workflows.
 */
export interface FeatureAddonDescriptor {
  key: FeatureAddonKey;
  label: string;
  description: string;
  /** Internal docs / spec pointer, optional. Surfaced as a small inline link. */
  docsHref?: string;
}

export const FEATURE_ADDON_CATALOG: readonly FeatureAddonDescriptor[] = [
  {
    key: FEATURE_ADDONS.ledger,
    label: "Books",
    description:
      "Bookkeeping in Relay: chart of accounts, journal, general ledger, trial balance and period close. " +
      "Keep this organization's own books, or record its activity in another organization's books under its own class.",
  },
];
